import { Injector } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { Mark, mergeAttributes } from '@tiptap/core';
import { createPopoverPlugin } from './popover-plugin';
import { generatePopoverId, POPOVER_ID_DATA_ATTR, PopoverAttributes } from './popover.model';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    popover: {
      /**
       * Set popover mark with content and optional link
       */
      setPopover: (attributes: PopoverAttributes) => ReturnType;
      /**
       * Remove popover mark
       */
      unsetPopover: () => ReturnType;
      /**
       * Toggle popover mark
       */
      togglePopover: (attributes: PopoverAttributes) => ReturnType;
      /**
       * Update popover mark attributes by ID
       */
      updatePopoverById: (id: string, attributes: Partial<PopoverAttributes>) => ReturnType;
    };
  }
}

export interface PopoverOptions {
  HTMLAttributes: Record<string, string>;
}

export const PopoverExtension = (injector: Injector) => {
  const errorHandlerService = injector.get(ErrorHandlerService);

  return Mark.create<PopoverOptions>({
    name: 'popover',

    addOptions() {
      return {
        HTMLAttributes: {},
      };
    },

    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute(POPOVER_ID_DATA_ATTR),
          renderHTML: (attributes) => {
            if (!attributes['id']) {
              return {};
            }
            return {
              [POPOVER_ID_DATA_ATTR]: attributes['id'],
            };
          },
        },
        jsonContent: {
          default: null,
          parseHTML: (element) => {
            const jsonContentStr = element.getAttribute('data-json-content');
            if (!jsonContentStr) {
              return null;
            }
            try {
              return JSON.parse(jsonContentStr);
            } catch (err) {
              errorHandlerService.error('Failed to parse popover JSON content', {
                context: { jsonContentStr },
                err,
              });
              return null;
            }
          },
          renderHTML: (attributes) => {
            if (!attributes['jsonContent']) {
              return {};
            }
            return {
              'data-json-content': JSON.stringify(attributes['jsonContent']),
            };
          },
        },
        linkUrl: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-link-url'),
          renderHTML: (attributes) => {
            if (!attributes['linkUrl']) {
              return {};
            }
            return {
              'data-link-url': attributes['linkUrl'],
            };
          },
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: 'span[data-popover]',
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return [
        'span',
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          'data-popover': '',
          class: 'popover-mark',
        }),
        0,
      ];
    },

    addCommands() {
      return {
        setPopover:
          (attributes) =>
          ({ commands }) => {
            // Auto-generate ID if not provided
            const attrsWithId = {
              ...attributes,
              id: attributes.id || generatePopoverId(),
            };
            return commands.setMark(this.name, attrsWithId);
          },
        unsetPopover:
          () =>
          ({ commands }) => {
            return commands.unsetMark(this.name);
          },
        togglePopover:
          (attributes) =>
          ({ commands }) => {
            // Auto-generate ID if not provided
            const attrsWithId = {
              ...attributes,
              id: attributes.id || generatePopoverId(),
            };
            return commands.toggleMark(this.name, attrsWithId);
          },
        updatePopoverById:
          (id, attributes) =>
          ({ tr, state, dispatch }) => {
            const { doc } = state;
            let updated = false;

            doc.descendants((node, pos) => {
              if (node.isText) {
                const marks = node.marks.filter((mark) => mark.type.name === 'popover' && mark.attrs['id'] === id);
                if (marks.length > 0) {
                  const mark = marks[0];
                  const newMark = mark.type.create({ ...mark.attrs, ...attributes });
                  tr.addMark(pos, pos + node.nodeSize, newMark);
                  updated = true;
                }
              }
            });

            if (updated && dispatch) {
              dispatch(tr);
            }

            return updated;
          },
      };
    },

    addProseMirrorPlugins() {
      return [createPopoverPlugin(this.editor)];
    },
  });
};
